export type ArrowEdge = 'top' | 'bottom' | 'left' | 'right'

export type PopupArrowInput = {
  anchorPosition: {top: number; left: number}
  contentSize: {width: number; height: number}
  windowWidth: number
  windowHeight: number
}

export type PopupArrowResult = {
  arrowEdge: ArrowEdge
  transformOriginVertical: 'top' | 'bottom' | 'center'
  transformOriginHorizontal: 'left' | 'right'
  anchorTopOffset: number
  anchorLeftOffset: number
}

/**
 * Computes the arrow edge and MUI Popover positioning for a popup,
 * so the arrow always points toward the anchor (click) position.
 *
 * Priority: below → above → right → left (fallback)
 */
// Distance we ask MUI's Popover to place the Paper away from the anchor
// (see anchorTopOffset/anchorLeftOffset below). The fits* checks below must
// include this offset — otherwise a case we consider "fits" can still trip
// Popover's own `marginThreshold` clamp, which then silently shifts the Paper
// away from the anchor while our arrow (pinned to the Paper's edge) doesn't
// move with it. Popup disables that clamp (`marginThreshold={0}`) so these
// checks are the sole source of truth for whether an edge is safe to use.
const VERTICAL_ANCHOR_OFFSET = 15
const HORIZONTAL_ANCHOR_OFFSET = 20
const SAFE_MARGIN = 10

export function computePopupArrow(
  {anchorPosition, contentSize, windowWidth, windowHeight}: PopupArrowInput,
  isLeft: boolean,
): PopupArrowResult {
  const fitsBelow =
    windowHeight - anchorPosition.top >=
    contentSize.height + VERTICAL_ANCHOR_OFFSET + SAFE_MARGIN
  const fitsAbove =
    anchorPosition.top >=
    contentSize.height + VERTICAL_ANCHOR_OFFSET + SAFE_MARGIN
  const fitsRight =
    windowWidth - anchorPosition.left >=
    contentSize.width + HORIZONTAL_ANCHOR_OFFSET + SAFE_MARGIN

  if (fitsBelow) {
    return {
      arrowEdge: 'top',
      transformOriginVertical: 'top',
      transformOriginHorizontal: isLeft ? 'left' : 'right',
      anchorTopOffset: 15,
      anchorLeftOffset: isLeft ? -20 : 20,
    }
  }

  if (fitsAbove) {
    return {
      arrowEdge: 'bottom',
      transformOriginVertical: 'bottom',
      transformOriginHorizontal: isLeft ? 'left' : 'right',
      anchorTopOffset: -15,
      anchorLeftOffset: isLeft ? -20 : 20,
    }
  }

  if (fitsRight) {
    return {
      arrowEdge: 'left',
      transformOriginVertical: 'center',
      transformOriginHorizontal: 'left',
      anchorTopOffset: 0,
      anchorLeftOffset: 15,
    }
  }

  return {
    arrowEdge: 'right',
    transformOriginVertical: 'center',
    transformOriginHorizontal: 'right',
    anchorTopOffset: 0,
    anchorLeftOffset: -15,
  }
}
